import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/query/queryKeys';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { recoveryCodeErrorMessage } from '@/lib/auth/recoveryCodes';
import { appCodeErrorMessage, owesAppCode } from '@/lib/auth/appCode';

/**
 * TwoStepGate: once a creator turns on two-factor authentication (Settings → Security),
 * every sign-in asks for the authenticator code before the app opens
 * (2026-10-03). Without this step a factor would be decoration: Supabase
 * issues a first-step session either way, and only the app can ask for more.
 *
 * A creator who lost the phone signs in with one of the recovery codes saved
 * at set-up instead; each works once.
 *
 * A passkey sign-in is a first step, as the email code and Google are, and
 * the app's code follows it (2026-10-13). Supabase holds a passkey session at
 * aal1 and acts on that: it deletes every session below aal2 after any second
 * step (mfa.go), and guards passkey registration only once a factor exists
 * (passkey_webauthn.go). Counting a passkey as both steps on top of that was
 * built and taken back (migrations 20261012225035, 20261013000407).
 *
 * The assurance level is read locally from the session
 * (`getAuthenticatorAssuranceLevel`). The gate opens only on an answer read
 * after this mount: not on a cached one (a previous session's "aal2" would
 * open it for a new email-code sign-in), and not while the persisted cache is
 * still restoring. If the first read fails, the gate stays closed and offers
 * a retry and a sign-out, never a way around; a later re-read that fails
 * keeps the app open on the answer it already has.
 */
export function TwoStepGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? '_';
  const queryClient = useQueryClient();
  const assurance = useQuery({
    queryKey: queryKeys.authAssurance(userId),
    enabled: !!user,
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw error;
      return data;
    },
  });

  if (assurance.isLoadingError) {
    return <GateMessage title="We could not check your sign-in" body="Try again. If it keeps happening, sign out and sign in again." onRetry={() => assurance.refetch()} />;
  }
  if (!assurance.isFetchedAfterMount || !assurance.data) {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-busy="true">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Checking your sign-in</span>
      </div>
    );
  }
  const needsCode = owesAppCode(assurance.data);
  // Passing the second step upgrades the session to aal2, and the rows follow
  // (migration 20261003200000: every table refuses an enrolled account at aal1).
  // Two things read with the old token must learn the new one: the live
  // channel, which supabase-js does not re-auth on MFA_CHALLENGE_VERIFIED (only
  // on SIGNED_IN / TOKEN_REFRESHED, read in its source 2026-10-03), and every
  // query fetched before the code, which saw no rows.
  const passed = async () => {
    await supabase.realtime.setAuth();
    await queryClient.invalidateQueries();
    await assurance.refetch();
  };
  if (needsCode) return <TwoStepChallenge onDone={() => void passed()} />;
  return <>{children}</>;
}

function GateMessage({ title, body, onRetry }: { title: string; body: string; onRetry: () => void }) {
  const { signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => void signOut()}>Sign out</Button>
          <Button onClick={onRetry}>Try again</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function TwoStepChallenge({ onDone }: { onDone: () => void }) {
  const { user, signOut } = useAuth();
  const userId = user?.id ?? '_';
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chooseCodes, setChooseCodes] = useState(false);
  // Which second steps this account holds: the app, the recovery codes, or both.
  const factors = useQuery({
    queryKey: queryKeys.authFactors(userId),
    enabled: !!user,
    queryFn: async () => {
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;
      return data;
    },
  });
  const hasApp = (factors.data?.totp.length ?? 0) > 0;
  const hasCodes = (factors.data?.recovery_code.length ?? 0) > 0;
  // Codes without an app (the app was removed elsewhere) open on the code field.
  const withCode = chooseCodes || (factors.isSuccess && !hasApp && hasCodes);

  const verifyApp = async (value: string) => {
    if (checking || value.length < 6) return;
    setChecking(true);
    setError(null);
    try {
      const factor = factors.data?.totp[0];
      if (!factor) throw new Error('No authenticator app is set up for this account.');
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({ factorId: factor.id, code: value });
      if (verifyError) throw verifyError;
      onDone();
    } catch (e) {
      setCode('');
      setError(appCodeErrorMessage(e));
    } finally {
      setChecking(false);
    }
  };

  const verifyRecoveryCode = async () => {
    if (checking || recoveryCode.trim() === '') return;
    setChecking(true);
    setError(null);
    const { error: verifyError } = await supabase.auth.mfa.recoveryCodes.verify({ code: recoveryCode });
    if (verifyError) {
      setChecking(false);
      setError(recoveryCodeErrorMessage(verifyError));
      return;
    }
    const { data: status } = await supabase.auth.mfa.recoveryCodes.getStatus();
    toast(status ? `Signed in with a recovery code. ${status.remaining} left.` : 'Signed in with a recovery code.', {
      description: 'If your phone is gone, set up two-factor authentication again in Settings → Security.',
    });
    setChecking(false);
    onDone();
  };

  const linkClass = 'text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted" aria-hidden="true">
            <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          </div>
          <CardTitle className="text-lg">Two-factor authentication</CardTitle>
          <CardDescription>
            {withCode
              ? 'Enter one of the recovery codes you saved when you turned on two-factor authentication.'
              : 'Enter the 6-digit code your authenticator app shows for Contruly.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {withCode ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void verifyRecoveryCode();
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <label htmlFor="two-step-recovery-code" className="text-sm font-medium">Recovery code</label>
                {/* Never disabled while the code is checked: a disabled field
                    drops focus, and a wrong code then needs a click first. */}
                <Input
                  id="two-step-recovery-code"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  autoFocus
                  autoComplete="one-time-code"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-invalid={error ? true : undefined}
                  aria-describedby="two-step-message"
                  className="font-mono aria-[invalid=true]:border-destructive"
                />
              </div>
              <p id="two-step-message" aria-live="polite" className={error ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
                {error ?? 'Each code works once. Dashes and capitals do not matter.'}
              </p>
              <Button type="submit" className="w-full" disabled={checking || recoveryCode.trim() === ''}>
                {checking ? 'Checking…' : 'Continue'}
              </Button>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void verifyApp(code);
              }}
              className="space-y-4"
            >
              <div className="flex justify-center">
                {/* Never disabled while the code is checked: a disabled field
                    drops focus, and a wrong code then needs a click first. */}
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={setCode}
                  onComplete={(value) => void verifyApp(value)}
                  autoFocus
                  aria-describedby="two-step-message"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <p id="two-step-message" aria-live="polite" className={error ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
                {error ?? 'The code changes every 30 seconds.'}
              </p>
              <Button type="submit" className="w-full" disabled={checking || code.length < 6}>
                {checking ? 'Checking…' : 'Continue'}
              </Button>
            </form>
          )}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            {hasApp && hasCodes ? (
              <button type="button" onClick={() => { setChooseCodes(!chooseCodes); setError(null); }} className={linkClass}>
                {chooseCodes ? 'Use your authenticator app' : 'Lost your phone? Use a recovery code'}
              </button>
            ) : <span />}
            <button type="button" onClick={() => void signOut()} className={linkClass}>
              Sign out
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default TwoStepGate;
